import { motion } from "framer-motion";
import { ChevronLeft } from "lucide-react";
import { StoryState } from "@/lib/story-store";

interface Props {
  state: StoryState;
  onUpdate: (partial: Partial<StoryState>) => void;
  onNext: () => void;
  onBack: () => void;
}

const lengths = [
  { id: "short" as const, label: "Небольшая", desc: "5–7 минут спокойного чтения" },
  { id: "medium" as const, label: "Средняя", desc: "10 минут погружения в историю" },
  { id: "long" as const, label: "Развёрнутая", desc: "15 минут с более глубоким сюжетом" },
];

const tones = [
  { id: "calm" as const, label: "Спокойная", desc: "Мягкая и плавная, идеально перед сном" },
  { id: "adventure" as const, label: "Приключенческая", desc: "Больше событий и открытий" },
  { id: "humor" as const, label: "С лёгким юмором", desc: "Немного улыбок и игровых моментов" },
];

const heroFormats = [
  { id: "child" as const, label: "Ребёнок — главный герой", desc: "Ваш ребёнок станет героем сказки" },
  { id: "companion" as const, label: "Герой, который учится вместе с ним", desc: "Отдельный персонаж проживает похожую историю" },
];

const StorySettings = ({ state, onUpdate, onNext, onBack }: Props) => {
  const goalColor = state.goal ? `var(--archetype-${state.goal})` : null;

  const cardStyle = (isSelected: boolean) => {
    if (!isSelected) return {};
    if (goalColor) {
      return {
        borderColor: `hsl(${goalColor})`,
        backgroundColor: `hsl(${goalColor} / 0.06)`,
      };
    }
    return {
      borderColor: "hsl(var(--foreground) / 0.3)",
      backgroundColor: "hsl(var(--foreground) / 0.04)",
    };
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6 py-12 relative">
      <button
        onClick={onBack}
        className="fixed top-4 left-4 z-50 w-9 h-9 rounded-full flex items-center justify-center text-foreground bg-card/70 backdrop-blur-sm hover:bg-card transition-colors"
        aria-label="Назад"
      >
        <ChevronLeft size={20} />
      </button>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-lg space-y-8"
      >
        <div className="space-y-2">
          <h2 className="text-2xl md:text-3xl font-serif text-foreground text-center">
            Какой будет эта история?
          </h2>
          <p className="text-sm text-muted-foreground font-sans text-center">
            Выберите формат и настроение
          </p>
        </div>

        {/* Формат героя */}
        <div className="space-y-3">
          <label className="font-sans font-semibold text-foreground" style={{ fontSize: "16px" }}>
            Как будет развиваться история?
          </label>
          <div className="grid gap-3">
            {heroFormats.map((h) => {
              const isSelected = state.heroFormat === h.id;
              return (
                <motion.button
                  key={h.id}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => onUpdate({ heroFormat: h.id })}
                  className={`text-left p-5 rounded-[20px] font-sans transition-all ${
                    isSelected
                      ? "border-2 shadow-md"
                      : "bg-card border border-border shadow-sm hover:shadow-md"
                  }`}
                  style={cardStyle(isSelected)}
                >
                  <span className="font-semibold text-sm text-foreground block">{h.label}</span>
                  <span className="text-xs text-muted-foreground mt-0.5 block">{h.desc}</span>
                </motion.button>
              );
            })}
          </div>
        </div>

        {/* Формат чтения */}
        <div className="space-y-3">
          <label className="font-sans font-semibold text-foreground" style={{ fontSize: "16px" }}>
            Формат чтения
          </label>
          <div className="grid gap-3">
            {lengths.map((l) => {
              const isSelected = state.length === l.id;
              return (
                <motion.button
                  key={l.id}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => onUpdate({ length: l.id })}
                  className={`text-left p-5 rounded-[20px] font-sans transition-all ${
                    isSelected
                      ? "border-2 shadow-md"
                      : "bg-card border border-border shadow-sm hover:shadow-md"
                  }`}
                  style={cardStyle(isSelected)}
                >
                  <span className="font-semibold text-sm text-foreground block">{l.label}</span>
                  <span className="text-xs text-muted-foreground mt-0.5 block">{l.desc}</span>
                </motion.button>
              );
            })}
          </div>
        </div>

        {/* Настроение истории */}
        <div className="space-y-3">
          <label className="font-sans font-semibold text-foreground" style={{ fontSize: "16px" }}>
            Настроение истории
          </label>
          <div className="grid gap-3">
            {tones.map((t) => {
              const isSelected = state.tone === t.id;
              return (
                <motion.button
                  key={t.id}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => onUpdate({ tone: t.id })}
                  className={`text-left p-5 rounded-[20px] font-sans transition-all ${
                    isSelected
                      ? "border-2 shadow-md"
                      : "bg-card border border-border shadow-sm hover:shadow-md"
                  }`}
                  style={cardStyle(isSelected)}
                >
                  <span className="font-semibold text-sm text-foreground block">{t.label}</span>
                  <span className="text-xs text-muted-foreground mt-0.5 block">{t.desc}</span>
                </motion.button>
              );
            })}
          </div>
        </div>

        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.97 }}
          onClick={onNext}
          className="w-full py-4 rounded-[28px] bg-primary text-primary-foreground font-sans font-semibold text-lg transition-opacity"
        >
          Далее
        </motion.button>
      </motion.div>
    </div>
  );
};

export default StorySettings;
